import { Form } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  type IntegrationToUpdate,
  type ModelSocialIntegration,
  useDeleteIntegrationMutation,
  useLazyGetSocialIntegrationsQuery,
  useRestartIntegrationMutation,
  useUpdateSocialIntegrationEmailHandlerMutation,
} from '@app/api/SocialIntegrations';
import {
  AUTHORIZING,
  INTEGRATIONS_PARSING_PATH,
  INTEGRATIONS_PATH,
  YOOKASSA,
} from '@app/constants';
import {
  RIGHTS_INTEGRATIONS,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_DELETE,
  RIGHTS_IS_EDIT,
  RIGHTS_IS_ON,
} from '@app/constants/accessRights';
import { ERROR, FRONT_WARNING, SUCCESS } from '@app/constants/alerts';
import {
  EMAIL_HANDLER,
  SMS_SERVICE,
  TECH_SUPPORT,
  WHATSAPP,
} from '@app/constants/socialIntegrationsTypes';
import { useAccessRights, useMemoizeObject, useUserPrint } from '@app/hooks';
import { useSalesFunnels } from '@app/hooks/useSalesFunnels';
import { IntegrationsContext, NotificationContext } from '@app/providers';
import {
  useDeleteIntegration1CMutation,
  useDeleteYookassaIntegrationMutation,
  useLazyGetSocialIntegrationOperatorsQuery,
} from '@app/services/rtkQuery/SocialIntegrations/api';
import { createWSConnection, subscribeToWsEvent } from '@app/utils';
import { invokeWSMethod } from '@app/utils/createWSConnection';

import { useWhatsAppAuthorization } from '@pages/Integrations/IntegrationsList/hooks/useWhatsAppAuthorization';

import type {
  EmailModalFormValues,
  MessengerModalFormValues,
} from '@pages/Integrations/IntegrationsList/types';

export const useIntegrations = () => {
  const { database, activeBranchId } = useUserPrint();

  const [isLoading, setIsLoading] = useState(false);
  const [modalCounter, setModalCounter] = useState(10);
  // Единое окно подключения канала (us/10721): какой интеграции показан шаг
  // «Авторизация». channelType — SocialType из CONNECT_CHANNEL_CONFIGS;
  // authUrl — OAuthUrl для архетипа «вход у провайдера» (ВКонтакте, Instagram).
  const [connectAuth, setConnectAuth] = useState<{
    channelType: string;
    integrationId: number;
    authUrl: string | null;
  } | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageFromUrl = searchParams.get('page');
  const yooKassaCode = searchParams.get('code');

  const [editIntegrationForm] = Form.useForm<MessengerModalFormValues>();
  const [createEmailIntegrationForm] = Form.useForm<EmailModalFormValues>();
  const [delete1CIntegration] = useDeleteIntegration1CMutation();

  const intervals = useRef<NodeJS.Timeout[]>([]);

  const {
    isWhatsAppModalOpen,
    setIsWhatsAppModalOpen,
    isEmailModalOpen,
    setIsEmailModalOpen,
    setCurrentIntegrationType,
    integrations: integrationData,
    setIntegrations,
    currentIntegrationType,
    setIsOAuthFrameOpen,
    setIsOpenAddWhatsAppIntegrationWarningModal,
    isOpenAddWhatsAppIntegrationWarningModal,
    setIsSocialMediaSettingsModalOpen,
    setIsYooKassaModalOpen,
    isAdd1CIntegrationModalOpen,
    setIsAdd1CIntegrationModalOpen,
    isEdit1CIntegrationModalOpen,
    setIsEdit1CIntegrationModalOpen,
    edit1CIntegrationId,
    setEdit1CIntegrationId,
    edit1CIntegrationMode,
    setEdit1CIntegrationMode,
    setCurrentYooKassaIntegrationId,
  } = useContext(IntegrationsContext);
  const [isReloadModalOpen, setIsReloadModalOpen] = useState(false);
  const [deletingModalOpen, setDeletingModalOpen] = useState(false);
  const [currentDeletingId, setCurrentDeletingId] = useState(0);
  const [updatingIntegrationId, setUpdatingIntegrationId] = useState(0);
  const [isReloadLoading, setIsReloadLoading] = useState(false);
  const [isEditMessengerModalOpen, setIsEditMessengerModalOpen] =
    useState(false);
  const [editableIntegration, setEditableIntegration] =
    useState<ModelSocialIntegration>();
  const [isEmailIntegrationLoading, setIsEmailIntegrationLoading] =
    useState(false);
  const [currentAuthLink, setCurrentAuthLink] = useState('');
  const [token, setToken] = useState<string | null>(null);

  const { showNotification } = useContext(NotificationContext);

  const {
    setSelectedSalesFunnelId,
    salesFunnels,
    salesFunnelsStages,
    isSalesFunnelsListLoading,
  } = useSalesFunnels();

  const integrations = useMemoizeObject(integrationData);

  const { hasUserRight: hasUserRightForModuleView } = useAccessRights(
    RIGHTS_INTEGRATIONS,
    RIGHTS_IS_ON
  );
  const { hasUserRight: hasUserRightToCreateIntegration } = useAccessRights(
    RIGHTS_INTEGRATIONS,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToEditIntegration } = useAccessRights(
    RIGHTS_INTEGRATIONS,
    RIGHTS_IS_EDIT
  );
  const { hasUserRight: hasUserRightToDeleteIntegration } = useAccessRights(
    RIGHTS_INTEGRATIONS,
    RIGHTS_IS_DELETE
  );

  const [getSocialIntegrationOperators] =
    useLazyGetSocialIntegrationOperatorsQuery();
  const [deleteYookassaIntegration] = useDeleteYookassaIntegrationMutation();
  const [getSocialIntegrations] = useLazyGetSocialIntegrationsQuery();
  const [deleteIntegration] = useDeleteIntegrationMutation();
  const [restartIntegration] = useRestartIntegrationMutation();
  const [updateSocialIntegrationEmailHandler] =
    useUpdateSocialIntegrationEmailHandlerMutation();

  useEffect(() => {
    if (!pageFromUrl) {
      navigate(
        `${INTEGRATIONS_PATH}?page=1${
          yooKassaCode ? `&code=${yooKassaCode}` : ''
        }`
      );
    }
  }, [navigate, pageFromUrl, searchParams, yooKassaCode]);

  useEffect(() => {
    if (yooKassaCode) {
      setIsYooKassaModalOpen(true);
    }
  }, [setIsYooKassaModalOpen, yooKassaCode]);

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getSocialIntegrations({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      setIntegrations(result);

      // Возвращаем список наружу: состояние обновится только к следующему
      // рендеру, а вызывающему коду свежие данные нужны сразу (issue/10918).
      return result;
    } catch (error) {
      showNotification(error as Error);

      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [
    activeBranchId,
    getSocialIntegrations,
    showNotification,
    setIntegrations,
  ]);

  // Единая сессия авторизации WhatsApp: и для новой интеграции, и для
  // повторного входа. Источник истины — состояние интеграции в CRM, событие по
  // вебсокету только ускоряет реакцию (issue/11029).
  const whatsAppAuth = useWhatsAppAuthorization({
    onSuccess: async () => {
      // Окно не закрываем: человек должен увидеть, что подключение удалось.
      // Реестр обновляем один раз — обработчик вызывается ровно однажды за
      // сессию, даже если событие и опрос состояния пришли одновременно.
      await getData();
      showNotification('Интеграция подключена', SUCCESS);
      setIsSocialMediaSettingsModalOpen(false);
    },
  });

  const openAuthWhatsAppModal = useCallback(
    async (id: number) => {
      try {
        // Список берём из ответа getData, а не из состояния: в замыкании лежит
        // значение ДО запроса, поэтому первое нажатие видело устаревший статус
        // и окно не открывалось (issue/10918).
        const freshIntegrations = await getData();
        const currentIntegration = (freshIntegrations ?? integrations)?.find(
          ({ Id }) => Id === id
        );
        if (!currentIntegration) return;
        const { Status, SocialType } = currentIntegration;

        // Вход по QR доступен и из «Ошибки». Основной путь — статус
        // «Подключается», который приходит после правки шлюза; эта ветка
        // страхует случай, когда до него дело не дошло, а другого выхода из
        // ошибки у пользователя нет (issue/10916, issue/10918).
        const canAuthorize = Status === AUTHORIZING || Status === ERROR;

        if (canAuthorize && SocialType === WHATSAPP) {
          setUpdatingIntegrationId(id);
          setIsWhatsAppModalOpen(true);
          // Идентификатор и токен берём из свежей строки реестра: раньше окно
          // опиралось на ответ запроса QR, которого при статусе «Ошибка» нет
          // вовсе (issue/11029).
          await whatsAppAuth.openExisting(currentIntegration);
        }
      } catch (error) {
        showNotification(error);
      }
    },
    [
      getData,
      integrations,
      setIsWhatsAppModalOpen,
      setUpdatingIntegrationId,
      showNotification,
      whatsAppAuth,
    ]
  );

  const openConnectAuth = useCallback(
    (channelType: string, integrationId: number, authUrl?: string | null) => {
      setConnectAuth({ channelType, integrationId, authUrl: authUrl ?? null });
    },
    []
  );

  const closeConnectAuth = useCallback(async () => {
    setConnectAuth(null);
    await getData();
  }, [getData]);

  const createHubConnection = useCallback(() => {
    createWSConnection(async () => {
      await invokeWSMethod('ListenOrganization', database);

      // Подписку на WhatsAppSenderQR держит useWhatsAppAuthorization: там
      // событие сверяется с токеном сессии и лишь ускоряет запрос состояния.
      // Здесь она читала поле param1, которого в контракте нет, и для
      // существующей интеграции не срабатывала никогда (issue/11029).

      subscribeToWsEvent('SocialIntegrationStateChanged', ({ Status }) => {
        if (Status !== ERROR) {
          setIsOAuthFrameOpen(false);
        }

        getData();
      });
    });
  }, [database, setIsOAuthFrameOpen, getData]);

  const delete1CIntegrationHandler = useCallback(
    async (integrationId: number) => {
      try {
        await delete1CIntegration({
          ActiveBranchId: activeBranchId,
          IntegrationId: integrationId,
        }).unwrap();
      } catch (error) {
        showNotification(error);
      } finally {
        await getData();
      }
    },
    [activeBranchId, delete1CIntegration, getData, showNotification]
  );

  const handleDeleteIntegration = useCallback(async () => {
    setIsLoading(true);
    const currentIntegration = integrations?.find(
      ({ Id }) => Id === currentDeletingId
    );

    try {
      if (currentIntegration?.SocialType === YOOKASSA) {
        await deleteYookassaIntegration({
          ActiveBranchId: activeBranchId,
          integrationId: currentDeletingId,
        }).unwrap();
      } else if (currentIntegration?.SocialType === 'warehouse-1c') {
        await delete1CIntegrationHandler(currentDeletingId);
      } else {
        await deleteIntegration({
          ActiveBranchId: activeBranchId,
          integrationId: currentDeletingId,
        }).unwrap();
      }

      showNotification('Интеграция удалена успешно', 'success');
    } catch (error) {
      showNotification(error as Error);
    } finally {
      await getData();
      setIsLoading(false);
      setDeletingModalOpen(false);
      setCurrentDeletingId(0);
    }
  }, [
    activeBranchId,
    currentDeletingId,
    deleteIntegration,
    deleteYookassaIntegration,
    getData,
    integrations,
    showNotification,
    delete1CIntegrationHandler,
  ]);

  const clearAllIntervalsAndTimeouts = () => {
    intervals.current.forEach(clearInterval);
    intervals.current = [];
    setUpdatingIntegrationId(0);
  };

  const handleRestartIntegration = async () => {
    setIsReloadLoading(true);

    try {
      await restartIntegration({
        ActiveBranchId: activeBranchId,
        integrationId: updatingIntegrationId,
      }).unwrap();
      showNotification('Интеграция перезагружена', SUCCESS);

      await getData();
    } catch (error) {
      showNotification(error as Error);
    } finally {
      clearAllIntervalsAndTimeouts();
      setIsReloadModalOpen(false);
      setIsReloadLoading(false);
    }
  };

  const handleEdit = useCallback(
    (id: number) => {
      const currentIntegration = integrations?.find((item) => item.Id === id);

      setEditableIntegration(currentIntegration);

      if (currentIntegration?.SocialType === EMAIL_HANDLER) {
        createEmailIntegrationForm.setFieldsValue({
          SocialIntegrationName:
            currentIntegration?.SocialIntegrationName ?? null,
          AccountLogin: currentIntegration?.AccountLogin,
          Password: currentIntegration?.Password,
          Server: currentIntegration?.Server,
          Port: currentIntegration?.Port,
          IsSsl: currentIntegration?.IsSsl,
          salesFunnel: currentIntegration?.SalesFunnelId,
          salesFunnelStage: currentIntegration?.SalesFunnelStageId,
          ParseUsingNeural: currentIntegration?.ParseUsingNeural ?? false,
        });

        if (currentIntegration?.SalesFunnelId) {
          setSelectedSalesFunnelId(currentIntegration?.SalesFunnelId);
        }

        setIsEmailModalOpen(true);
      } else if (currentIntegration?.SocialType === SMS_SERVICE) {
        if (currentIntegration?.SalesFunnelId) {
          setSelectedSalesFunnelId(currentIntegration?.SalesFunnelId);
        }

        setCurrentIntegrationType(SMS_SERVICE);
        setIsSocialMediaSettingsModalOpen(true);
      } else {
        setToken(currentIntegration?.Token || null);

        if (currentIntegration?.SalesFunnelId) {
          setSelectedSalesFunnelId(currentIntegration?.SalesFunnelId);
        }

        if (!activeBranchId) {
          return showNotification(
            'Идентификатор текущего филиала не определен',
            FRONT_WARNING
          );
        }

        // У «Технической поддержки» операторы не редактируются (US 11021) —
        // не грузим их ни здесь, ни в самой модалке.
        if (currentIntegration?.SocialType !== TECH_SUPPORT) {
          getSocialIntegrationOperators({
            ActiveBranchId: activeBranchId,
            integrationId: id,
          });
        }

        setIsEditMessengerModalOpen(true);
      }
    },
    [
      activeBranchId,
      createEmailIntegrationForm,
      getSocialIntegrationOperators,
      integrations,
      showNotification,
      setIsEmailModalOpen,
      setSelectedSalesFunnelId,
      setCurrentIntegrationType,
      setIsSocialMediaSettingsModalOpen,
    ]
  );

  const onEditEmailSalesFunnelChange = (value: number) => {
    setSelectedSalesFunnelId(value);
    createEmailIntegrationForm.setFieldsValue({
      salesFunnelStage: null,
    });
  };

  const submitEmailModalForm = async ({
    SocialIntegrationName,
    Port,
    AccountLogin,
    Password,
    Server,
    IsSsl,
    salesFunnel,
    salesFunnelStage,
    ParseUsingNeural,
  }: EmailModalFormValues) => {
    setIsEmailIntegrationLoading(true);

    const handledPort = Port;
    const isPortNumber = handledPort?.toString()?.match(/[0-9]/g);

    if (!isPortNumber || handledPort === null)
      return showNotification(
        'Порт должен состоять только из цифр',
        FRONT_WARNING
      );

    const generalProps = {
      SocialIntegrationName,
      AccountLogin,
      Password,
      Server,
      IsSsl: Boolean(IsSsl),
      Port: +handledPort,
      SalesFunnelId: salesFunnel,
      SalesFunnelStageId: salesFunnelStage,
      ParseUsingNeural,
    };

    const data: IntegrationToUpdate = editableIntegration
      ? {
          ...editableIntegration,
          ...generalProps,
        }
      : generalProps;

    try {
      await updateSocialIntegrationEmailHandler({
        ActiveBranchId: activeBranchId,
        data,
      }).unwrap();
      showNotification('Интеграция добавлена успешно', SUCCESS);

      createEmailIntegrationForm.resetFields();
      setIsEmailIntegrationLoading(false);
      setIsEmailModalOpen(false);
      setIsSocialMediaSettingsModalOpen(false);
      await getData();
      setEditableIntegration(undefined);
    } catch (error) {
      showNotification(error as Error);
    }
  };

  useEffect(() => {
    getData();
  }, [activeBranchId, getData]);

  useEffect(() => {
    const handleAddIntegration = async () => {
      if (isWhatsAppModalOpen) {
        try {
          if (updatingIntegrationId) {
            // Опрос QR и состояния ведёт useWhatsAppAuthorization: он же
            // останавливает их при успехе, ошибке и закрытии окна. Прежний
            // ручной интервал жил здесь и в SocialMediaSettingsModal, и ни
            // один из них не знал об успешном входе (issue/11029).
            createHubConnection();

            await getData();
          }
        } catch (error) {
          showNotification(error as Error);
          setIsWhatsAppModalOpen(false);
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleAddIntegration();

    return () => {
      intervals.current.forEach(clearInterval);
      intervals.current = [];
    };
  }, [
    activeBranchId,
    createHubConnection,
    currentIntegrationType,
    database,
    getData,
    isWhatsAppModalOpen,
    showNotification,
    setIsWhatsAppModalOpen,
    updatingIntegrationId,
  ]);

  const setInitialSalesFunnel = () => {
    if (salesFunnels.length === 1) {
      setSelectedSalesFunnelId(salesFunnels[0].value as number);

      return salesFunnels[0].value as number;
    } else {
      return undefined;
    }
  };

  const openEmailHandlerParsingSettingsPage = useCallback(
    (id: number) => navigate(`${INTEGRATIONS_PARSING_PATH}/${id}`),
    [navigate]
  );

  const openEditYooKassaModal = useCallback(
    (id: number) => {
      setIsYooKassaModalOpen(true);
      setCurrentYooKassaIntegrationId(id);
    },
    [setCurrentYooKassaIntegrationId, setIsYooKassaModalOpen]
  );

  return {
    isLoading,
    integrations,
    handleDeleteIntegration,
    isWhatsAppModalOpen,
    setIsWhatsAppModalOpen,
    isEmailModalOpen,
    setIsEmailModalOpen,
    modalCounter,
    deletingModalOpen,
    setDeletingModalOpen,
    setCurrentDeletingId,
    clearAllIntervalsAndTimeouts,
    setUpdatingIntegrationId,
    updatingIntegrationId,
    isReloadModalOpen,
    setIsReloadModalOpen,
    handleRestartIntegration,
    isReloadLoading,
    setCurrentIntegrationType,
    isEditMessengerModalOpen,
    setIsEditMessengerModalOpen,
    editIntegrationForm,
    hasUserRightForModuleView,
    hasUserRightToCreateIntegration,
    hasUserRightToEditIntegration,
    hasUserRightToDeleteIntegration,
    createEmailIntegrationForm,
    isEmailIntegrationLoading,
    submitEmailModalForm,
    handleEdit,
    editableIntegration,
    setEditableIntegration,
    onEditEmailSalesFunnelChange,
    salesFunnels,
    salesFunnelsStages,
    isSalesFunnelsListLoading,
    getData,
    setSelectedSalesFunnelId,
    currentAuthLink,
    setCurrentAuthLink,
    createHubConnection,
    setModalCounter,
    setIsOAuthFrameOpen,
    showNotification,
    setInitialSalesFunnel,
    token,
    setToken,
    setIsOpenAddWhatsAppIntegrationWarningModal,
    isOpenAddWhatsAppIntegrationWarningModal,
    setIsSocialMediaSettingsModalOpen,
    openEmailHandlerParsingSettingsPage,
    openEditYooKassaModal,
    isAdd1CIntegrationModalOpen,
    setIsAdd1CIntegrationModalOpen,
    isEdit1CIntegrationModalOpen,
    setIsEdit1CIntegrationModalOpen,
    edit1CIntegrationId,
    setEdit1CIntegrationId,
    edit1CIntegrationMode,
    setEdit1CIntegrationMode,
    openAuthWhatsAppModal,
    whatsAppAuth,
    connectAuth,
    openConnectAuth,
    closeConnectAuth,
    activeBranchId,
  };
};
